import React, { useCallback, useEffect, useMemo } from "react";
import Container from "react-bootstrap/Container";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";

import { Button } from "@/components/Buttons";
import Table from "@/components/Table";
import {
  DISPOSE_APPROVAL_TYPE,
  renderDisposeApprovalType,
  renderDisposeApprovedAt,
} from "@/Models/DisposeApproval";
import { DeliveryForRiderModal } from "@/Modals";
import {
  useLoadingStore,
  useLostDeliveriesDisposeApprovalsStore,
  useModalStore,
} from "@/store/hooks";

const Title = styled.h1`
  font-size: 36px;
  margin-bottom: 20px;
`;

const StyledRow = styled(Row)`
  margin-left: -5px;
  margin-right: -5px;
  margin-bottom: 5px;
`;

const StyledCol = styled(Col)`
  padding-left: 5px;
  padding-right: 5px;
`;

const SearchRow = styled.div`
  margin-bottom: 10px;
`;

const DatePickerCol = styled(StyledCol)`
  display: flex;
  flex-direction: row;
  align-items: center;

  input {
    width: calc(50% - 15px);
  }
`;

const Wave = styled.span`
  margin: 0 5px;
`;

const ButtonWrap = styled.div`
  display: flex;
  margin-bottom: 10px;
`;

const StyledButton = styled(Button)`
  margin-right: 10px;
  min-width: 120px;
`;

const ClickableColumn = styled.span`
  text-decoration: underline;
  cursor: pointer;
`;

const MetaRow = styled.div`
  margin-bottom: 8px;
  font-size: 14px;
`;

const BookIdColumn = ({ delivery }) => {
  const { openModal } = useModalStore();

  function handleClick() {
    openModal(
      <DeliveryForRiderModal
        backdrop
        key="book-detail-modal"
        bookId={delivery.bookId}
        type={delivery.type || "1"}
      />,
    );
  }

  return (
    <ClickableColumn onClick={handleClick}>
      {delivery.renderBookId ? delivery.renderBookId() : delivery.bookId}
    </ClickableColumn>
  );
};

const LostDeliveriesDisposeApprovals = () => {
  const { state, ...actions } = useLostDeliveriesDisposeApprovalsStore();
  const { finishLoading, startLoading } = useLoadingStore();
  const { handleSubmit, register, reset, setValue } = useForm();

  useEffect(() => {
    reset({
      dateFrom: state.query.dateFrom,
      dateTo: state.query.dateTo,
      bookId: state.query.bookId,
      orderIdFromCorp: state.query.orderIdFromCorp,
      orderIdFromCorpSub: state.query.orderIdFromCorpSub,
    });
  }, []);

  useEffect(() => {
    fetchAll();
  }, [
    state.query.page,
    state.query.pageSize,
    state.query.dateFrom,
    state.query.dateTo,
    state.query.bookId,
    state.query.orderIdFromCorp,
    state.query.orderIdFromCorpSub,
    state.query.disposeApprovalType,
  ]);

  const fetchAll = async () => {
    try {
      startLoading();
      await actions.fetchAll();
    } catch (e) {
      window.alert(
        `폐기승인 내역을 불러오는데 실패하였습니다.\n에러메시지: ${e.message}`,
      );
    } finally {
      finishLoading();
    }
  };

  const handleGoToPage = useCallback(
    (page) => {
      actions.setQuery({ page });
    },
    [actions.setQuery],
  );

  const handleNextPage = useCallback(() => {
    actions.setQuery({ page: state.query.page + 1 });
  }, [actions.setQuery, state.query.page]);

  const handlePreviousPage = useCallback(() => {
    actions.setQuery({ page: state.query.page - 1 });
  }, [actions.setQuery, state.query.page]);

  const handleSetPageSize = useCallback(
    (pageSize) => {
      actions.setQuery({ page: 1, pageSize });
    },
    [actions.setQuery],
  );

  const handleSetDisposeApprovalType = useCallback(
    (disposeApprovalType) => {
      actions.setQuery({
        disposeApprovalType,
        page: 1,
      });
    },
    [actions.setQuery],
  );

  const onSubmit = useCallback(
    ({ dateFrom, dateTo, bookId, orderIdFromCorp, orderIdFromCorpSub }) => {
      actions.setQuery({
        dateFrom,
        dateTo,
        bookId,
        orderIdFromCorp,
        orderIdFromCorpSub,
        page: 1,
      });
    },
    [actions.setQuery],
  );

  const handleReset = useCallback(() => {
    const query = actions.resetQuery();
    reset({
      dateFrom: query.dateFrom,
      dateTo: query.dateTo,
      bookId: "",
      orderIdFromCorp: "",
      orderIdFromCorpSub: "",
    });
    setValue("dateFrom", query.dateFrom);
    setValue("dateTo", query.dateTo);
  }, [actions.resetQuery, reset, setValue]);

  const columns = useMemo(
    () => [
      {
        Header: "예약번호",
        accessor: (row) => <BookIdColumn delivery={row} />,
        selectable: false,
        width: 140,
      },
      {
        Header: "접수일시",
        accessor: (row) => row.renderReceiptDate(),
        width: 120,
      },
      {
        Header: "파손사유",
        accessor: (row) => row.damageReason || "",
        selectable: false,
        width: 180,
        ellipsis: false,
      },
      {
        Header: "폐기승인구분",
        accessor: (row) => renderDisposeApprovalType(row.disposeApprovalType),
        width: 130,
      },
      {
        Header: "폐기승인일시",
        accessor: (row) => renderDisposeApprovedAt(row.disposeApprovedAt),
        width: 150,
      },
      {
        Header: "사측주문번호",
        accessor: (row) => row.orderIdFromCorp || "",
        width: 140,
      },
      {
        Header: "주문자구분번호",
        accessor: (row) => row.orderIdFromCorpSub || "",
        width: 130,
      },
      {
        Header: "기업회원",
        accessor: (row) => row.corpUserCorpTitle,
        width: 150,
      },
      {
        Header: "수지",
        accessor: (row) => row.renderPickupDateScheduled("HH:mm"),
        width: 60,
      },
      {
        Header: "수완",
        accessor: (row) => row.renderPickupDateCompleted("HH:mm"),
        width: 60,
      },
      {
        Header: "입고",
        accessor: (row) => row.renderWarehousedAt("HH:mm"),
        width: 60,
      },
      {
        Header: "배차",
        accessor: (row) => row.renderDeliveryAllocatedDate(),
        width: 90,
      },
      {
        Header: "연기",
        accessor: (row) => row.renderPostponedDate(),
        width: 90,
      },
      {
        Header: "배출",
        accessor: (row) => row.renderReleasedAt(),
        width: 90,
      },
      {
        Header: "배완",
        accessor: (row) => row.renderDeliveryCompletedDate(),
        width: 90,
      },
      {
        Header: "출발동",
        accessor: (row) => row.senderDongName,
        width: 90,
      },
      {
        Header: "송하인",
        accessor: (row) => row.senderName,
        width: 150,
      },
      {
        Header: "지역",
        accessor: (row) =>
          `${row.receiverSidoName || ""} ${row.receiverSigunguName || ""}`.trim(),
        width: 150,
      },
      {
        Header: "도착동",
        accessor: (row) => row.receiverDongName,
        width: 90,
      },
      {
        Header: "도로명",
        accessor: (row) => row.receiverAddressRoadWithoutSigungu,
        width: 150,
      },
      {
        Header: "수거 담당",
        accessor: (row) => row.pickupRiderName,
        width: 80,
      },
      {
        Header: "배송 담당",
        accessor: (row) => row.deliveryRiderName,
        width: 80,
      },
      {
        Header: "메인운송장",
        accessor: (row) => (row.printedOnAdmin ? "O" : "X"),
        width: 80,
      },
      {
        Header: "서브운송장",
        accessor: (row) => (row.printedOnSubAdmin ? "O" : "X"),
        width: 80,
      },
    ],
    [],
  );

  return (
    <Container fluid>
      <Title>한진 화주사 폐기요청건 내역</Title>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <SearchRow>
          <StyledRow>
            <StyledCol md={2}>
              <Form.Control as="select" custom disabled>
                <option>폐기승인일</option>
              </Form.Control>
            </StyledCol>
            <DatePickerCol md={4}>
              <Form.Control name="dateFrom" type="date" ref={register} />
              <Wave>~</Wave>
              <Form.Control name="dateTo" type="date" ref={register} />
            </DatePickerCol>
            <StyledCol md={3}>
              <Form.Control
                name="bookId"
                placeholder="송장번호(bookId)"
                ref={register}
              />
            </StyledCol>
            <StyledCol md={3}>
              <Form.Control
                name="orderIdFromCorp"
                placeholder="사측주문번호"
                ref={register}
              />
            </StyledCol>
          </StyledRow>
          <StyledRow>
            <StyledCol md={3}>
              <Form.Control
                name="orderIdFromCorpSub"
                placeholder="주문자구분번호"
                ref={register}
              />
            </StyledCol>
            <StyledCol md={2}>
              <Button
                type="button"
                variant="light"
                style={{ width: "100%" }}
                onClick={handleReset}
              >
                초기화
              </Button>
            </StyledCol>
            <StyledCol md={2}>
              <Button type="submit" style={{ width: "100%" }}>
                조회
              </Button>
            </StyledCol>
          </StyledRow>
        </SearchRow>
      </Form>

      <ButtonWrap>
        <StyledButton
          onClick={() => handleSetDisposeApprovalType("")}
          variant={state.query.disposeApprovalType === "" ? "info" : "light"}
        >
          전체
        </StyledButton>
        <StyledButton
          onClick={() =>
            handleSetDisposeApprovalType(DISPOSE_APPROVAL_TYPE.MANUAL.value)
          }
          variant={
            state.query.disposeApprovalType ===
            DISPOSE_APPROVAL_TYPE.MANUAL.value
              ? "info"
              : "light"
          }
        >
          수동 폐기승인
        </StyledButton>
        <StyledButton
          onClick={() =>
            handleSetDisposeApprovalType(DISPOSE_APPROVAL_TYPE.AUTO.value)
          }
          variant={
            state.query.disposeApprovalType === DISPOSE_APPROVAL_TYPE.AUTO.value
              ? "info"
              : "light"
          }
        >
          자동 폐기승인
        </StyledButton>
      </ButtonWrap>

      <MetaRow>
        <span>전체 {state.totalCount || 0}건</span>
      </MetaRow>

      <Table
        checkbox={false}
        responsive
        bordered
        columns={columns}
        currentPage={state.query.page}
        data={state.rows}
        goToPage={handleGoToPage}
        nextPage={handleNextPage}
        pageCount={state.pageCount}
        pageSize={state.query.pageSize}
        previousPage={handlePreviousPage}
        setPageSize={handleSetPageSize}
      />
    </Container>
  );
};

export default LostDeliveriesDisposeApprovals;
